import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
const features = [{
  title: "Recognition & Rewards",
  description: "Recognize teammates with peer shout-outs and rewards that reflect your culture.",
  bgColor: "bg-gradient-to-br from-yellow-50 to-orange-50",
  textColor: "text-gray-800",
  link: "/employee-recognition/rewards",
  image: "/lovable-uploads/9f39e4ff-5ccc-43ee-8bea-fa747ca248d6.png"
}, {
  title: "Celebrations & Milestones",
  description: "Celebrate birthdays, anniversaries, and new hires automatically — no manual effort needed.",
  bgColor: "bg-white",
  textColor: "text-gray-800",
  link: "/employee-recognition/milestones",
  image: "/lovable-uploads/1ebb6afc-ba22-48cf-9f02-33dad767131e.png"
}, {
  title: "Awards & Challenges",
  description: "Launch custom awards and challenges that bring your values to life and spark engagement.",
  bgColor: "bg-gradient-to-br from-blue-50 to-indigo-50",
  textColor: "text-gray-800",
  link: "/employee-recognition/awards",
  image: "/lovable-uploads/45849da1-9d4d-46a5-8869-b48feda2ff4a.png"
}];
const FeaturesOverview = () => {
  const [imagesPreloaded, setImagesPreloaded] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Aggressively preload feature images immediately
  useEffect(() => {
    let loadedCount = 0;
    const totalImages = features.length;
    features.forEach(feature => {
      const img = new Image();
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          setImagesPreloaded(true);
        }
      };
      img.src = feature.image;
    });
  }, []);
  return <section className="pt-16 pb-20 2xl:pt-24 2xl:pb-32 my-0 py-[74px] 2xl:py-[120px]">
      <div className="container mx-auto px-4 2xl:px-12 max-w-none 2xl:max-w-[1600px]">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-12">
          <motion.h2 className="text-3xl sm:text-5xl md:text-5xl lg:text-5xl xl:text-5xl font-bold mb-4 leading-tight" initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: "easeOut"
        }}>
            Everything You Need to<br />
            <span className="text-blue-600">Recognize</span>, <span className="text-blue-600">Reward</span>, and <span className="text-blue-600">Retain</span> Your Team
          </motion.h2>
          <p className="text-lg 2xl:text-2xl text-gray-600 max-w-3xl 2xl:max-w-5xl mx-auto">From peer shout-outs to real rewards, Assembly's award-winning employee recognition software makes appreciation easy — helping you build a culture that lasts.</p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12 lg:gap-6 2xl:gap-12 max-w-6xl 2xl:max-w-none mx-auto">
          {features.map((feature, index) => <div key={index} className="relative">
              {/* Main Card - keeps its rounded corners */}
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`relative z-10 ${feature.bgColor} rounded-2xl p-6 2xl:p-10 transition-all duration-300 group border border-gray-100 shadow-sm hover:shadow-xl h-[480px] 2xl:h-[600px]`} onMouseEnter={() => setHoveredCard(index)} onMouseLeave={() => setHoveredCard(null)}>
                {/* Main Content */}
                <div className="relative z-10 flex flex-col h-full">
                  <h3 className={`text-xl lg:text-2xl 2xl:text-3xl font-bold ${feature.textColor} mb-4 leading-tight`}>
                    {feature.title}
                  </h3>
                  <p className={`${feature.textColor} text-sm 2xl:text-base mb-6 leading-relaxed opacity-80`}>
                    {feature.description}
                  </p>

                  {/* Feature Image */}
                  <div className="relative z-10 flex justify-center items-center h-96 mt-auto">
                    <img src={feature.image} alt={feature.title} className={`max-w-full max-h-full object-contain transition-all duration-500 ${imagesPreloaded ? 'opacity-100' : 'opacity-0'} group-hover:scale-105`} loading="eager" decoding="async" />
                    {!imagesPreloaded && <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-lg" />}
                  </div>
                </div>

                {/* Background decorations */}
                {index === 0 && <div className="absolute top-6 right-6 w-16 h-16 bg-orange-200/30 rounded-full blur-xl"></div>}
                {index === 2 && <div className="absolute top-6 right-6 w-20 h-20 bg-blue-200/30 rounded-full blur-xl"></div>}
              </motion.div>

              {/* Button that slides out from behind the card - overlapping slightly */}
              <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: hoveredCard === index || isMobile ? 60 : 0,
            opacity: hoveredCard === index || isMobile ? 1 : 0
          }} transition={{
            duration: 0.3,
            ease: "easeOut"
          }} className={`absolute -top-2 left-0 right-0 overflow-hidden rounded-b-2xl pt-2 ${index === 0 ? 'bg-orange-200' : index === 1 ? 'bg-gray-200' : 'bg-blue-200'}`} style={{
            top: 'calc(100% - 20px)'
          }} onMouseEnter={() => setHoveredCard(index)} onMouseLeave={() => setHoveredCard(null)}>
                <div className="p-4">
                  <a href={feature.link} title={`Learn more about ${feature.title}`} className={`w-full flex items-center justify-center font-medium transition-all duration-200 text-sm py-2 cursor-pointer ${index === 0 ? 'text-orange-900 hover:text-orange-800' : index === 1 ? 'text-gray-900 hover:text-gray-800' : 'text-blue-900 hover:text-blue-800'}`}>
                    See how it works
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </motion.div>
            </div>)}
        </div>
      </div>
    </section>;
};
export default FeaturesOverview;